'use client';

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';

interface ClientData {
  id: string;
  name: string | null;
  card_type: string | null;
  department: string | null;
  subscription_end: string | null;
  national_id: string | null;
  city: string | null;
}

interface PartnerVisitRecord {
  id: number;
  scanned_at: string;
  description: string | null;
  amount: number | null;
  amount_before_discount: number | null;
  amount_after_discount: number | null;
  client_name: string | null;
  client_national_id: string | null;
}

const PartnerDashboard = () => {
  const { user, signOut } = useAuth();
  const router = useRouter();
  const supabase = createClient();

  const [nationalId, setNationalId] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [clientData, setClientData] = useState<ClientData | null>(null);
  const [isLoggingVisit, setIsLoggingVisit] = useState(false);
  const [visitLogged, setVisitLogged] = useState(false);
  const [amountBeforeDiscount, setAmountBeforeDiscount] = useState('');
  const [amountAfterDiscount, setAmountAfterDiscount] = useState('');
  const [isSavingAmount, setIsSavingAmount] = useState(false);
  const [amountSaved, setAmountSaved] = useState(false);
  const [lastVisitId, setLastVisitId] = useState<number | null>(null);
  const [partnerVisits, setPartnerVisits] = useState<PartnerVisitRecord[]>([]);
  const [isLoadingPartnerVisits, setIsLoadingPartnerVisits] = useState(false);

  React.useEffect(() => {
    if (user?.id) {
      fetchPartnerVisits();
    }
  }, [user?.id]);

  const fetchPartnerVisits = async () => {
    if (!user?.id) return;
    setIsLoadingPartnerVisits(true);
    try {
      const { data, error } = await supabase
        .from('visit')
        .select('id, scanned_at, description, amount, amount_before_discount, amount_after_discount, client_id')
        .eq('scanned_by', user.id)
        .order('scanned_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      if (data && data.length > 0) {
        const clientIds = [...new Set(data.map((v: any) => v.client_id).filter(Boolean))];
        const { data: clientsData } = await supabase
          .from('clients')
          .select('id, name, national_id')
          .in('id', clientIds);

        const clientMap: Record<string, { name: string | null; national_id: string | null }> = {};
        if (clientsData) {
          clientsData.forEach((c: any) => {
            clientMap[c.id] = { name: c.name, national_id: c.national_id };
          });
        }

        const enriched: PartnerVisitRecord[] = data.map((v: any) => ({
          id: v.id,
          scanned_at: v.scanned_at,
          description: v.description,
          amount: v.amount,
          amount_before_discount: v.amount_before_discount,
          amount_after_discount: v.amount_after_discount,
          client_name: clientMap[v.client_id]?.name ?? null,
          client_national_id: clientMap[v.client_id]?.national_id ?? null,
        }));
        setPartnerVisits(enriched);
      } else {
        setPartnerVisits([]);
      }
    } catch {
      setPartnerVisits([]);
    } finally {
      setIsLoadingPartnerVisits(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    router.push('/homepage');
  };

  const isSubscriptionActive = (endDate: string | null): boolean => {
    if (!endDate) return false;
    return new Date(endDate) >= new Date();
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nationalId.trim()) return;

    setIsSearching(true);
    setSearchError('');
    setClientData(null);
    setVisitLogged(false);
    setAmountBeforeDiscount('');
    setAmountAfterDiscount('');
    setAmountSaved(false);
    setLastVisitId(null);

    try {
      const { data: client, error: clientError } = await supabase
        .from('clients')
        .select('id, name, card_type, department, subscription_end, national_id, city')
        .eq('national_id', nationalId.trim())
        .maybeSingle();

      if (clientError) throw clientError;

      if (!client) {
        setSearchError('لم يتم العثور على عميل بهذا الرقم القومي');
        return;
      }

      setClientData(client);

      await logVisit(client.id);
    } catch (err: any) {
      setSearchError(err.message || 'حدث خطأ أثناء البحث');
    } finally {
      setIsSearching(false);
    }
  };

  const logVisit = async (clientId: string) => {
    if (!user?.id) return;
    setIsLoggingVisit(true);
    try {
      const { data, error } = await supabase.from('visit').insert({
        client_id: clientId,
        scanned_by: user.id,
        description: 'زيارة شريك',
        amount: 0,
        amount_before_discount: 0,
        amount_after_discount: 0,
      }).select('id').single();
      if (!error && data) {
        setLastVisitId(data.id);
      }
      setVisitLogged(true);
      fetchPartnerVisits();
    } catch {
      // Silent fail for visit logging
    } finally {
      setIsLoggingVisit(false);
    }
  };

  const handleSaveAmount = async () => {
    if (!lastVisitId) return;
    const before = parseFloat(amountBeforeDiscount);
    const after = parseFloat(amountAfterDiscount);
    if (isNaN(before) || isNaN(after) || before < 0 || after < 0) return;

    setIsSavingAmount(true);
    try {
      await supabase.from('visit').update({
        amount_before_discount: before,
        amount_after_discount: after,
        amount: after,
      }).eq('id', lastVisitId);
      setAmountSaved(true);
      fetchPartnerVisits();
    } catch {
      // Silent fail
    } finally {
      setIsSavingAmount(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('ar-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCardTypeLabel = (cardType: string | null) => {
    if (!cardType) return '—';
    const map: Record<string, string> = {
      'vip': 'VIP',
      'عائلي سنوي': 'عائلي سنوي',
      'عائلي ربع سنوي': 'عائلي ربع سنوي',
      'شخصي': 'شخصي',
      // legacy fallbacks
      family: 'عائلي سنوي',
      personal: 'شخصي',
      corporate: 'مؤسسي',
    };
    return map[cardType] || map[cardType.toLowerCase()] || cardType;
  };

  const getCardTypeBadgeClass = (cardType: string | null) => {
    if (!cardType) return 'bg-muted text-muted-foreground';
    const map: Record<string, string> = {
      'vip': 'bg-yellow-100 text-yellow-800 border border-yellow-300',
      'عائلي سنوي': 'bg-blue-100 text-blue-800 border border-blue-300',
      'عائلي ربع سنوي': 'bg-indigo-100 text-indigo-800 border border-indigo-300',
      'شخصي': 'bg-green-100 text-green-800 border border-green-300',
      // legacy fallbacks
      family: 'bg-blue-100 text-blue-800 border border-blue-300',
      personal: 'bg-green-100 text-green-800 border border-green-300',
      corporate: 'bg-purple-100 text-purple-800 border border-purple-300',
    };
    return map[cardType] || map[cardType.toLowerCase()] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      {/* Header */}
      <header className="bg-card shadow-sm border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="BuildingStorefrontIcon" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground font-amiri">بوابة الشركاء</h1>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-error/10 text-error hover:bg-error/20 transition-premium"
            >
              <Icon name="ArrowRightOnRectangleIcon" size={20} />
              <span>تسجيل الخروج</span>
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Search Section */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-8">
          <h2 className="text-lg font-bold text-foreground font-amiri mb-4 flex items-center gap-2">
            <Icon name="MagnifyingGlassIcon" size={22} className="text-primary" />
            البحث عن عميل برقم الهوية
          </h2>
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="flex-1 relative">
              <input
                type="text"
                value={nationalId}
                onChange={(e) => setNationalId(e.target.value)}
                placeholder="أدخل رقم الهوية الوطنية"
                className="w-full px-4 py-3 pr-12 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium"
                disabled={isSearching}
              />
              <Icon
                name="IdentificationIcon"
                size={20}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
            </div>
            <button
              type="submit"
              disabled={isSearching || !nationalId.trim()}
              className="flex items-center gap-2 px-6 py-3 rounded-md bg-primary text-primary-foreground font-medium transition-premium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSearching ? (
                <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
              ) : (
                <Icon name="MagnifyingGlassIcon" size={20} />
              )}
              <span>{isSearching ? 'جاري البحث...' : 'بحث'}</span>
            </button>
          </form>

          {searchError && (
            <div className="mt-4 flex items-center gap-3 p-4 rounded-md bg-error/10 border border-error/20">
              <Icon name="ExclamationCircleIcon" size={20} className="text-error flex-shrink-0" />
              <p className="text-sm text-error">{searchError}</p>
            </div>
          )}
        </div>

        {/* Client Results */}
        {clientData && (
          <>
            {/* Client Info Card */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
              <div className="flex items-start justify-between mb-6">
                <h3 className="text-lg font-bold text-foreground font-amiri flex items-center gap-2">
                  <Icon name="UserCircleIcon" size={22} className="text-primary" />
                  بيانات العميل
                </h3>
                <div className="flex items-center gap-2">
                  {/* Card Type Badge */}
                  {clientData.card_type && (
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getCardTypeBadgeClass(clientData.card_type)}`}>
                      {getCardTypeLabel(clientData.card_type)}
                    </span>
                  )}
                  {/* Subscription Status Badge */}
                  <div
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                      isSubscriptionActive(clientData.subscription_end)
                        ? 'bg-success/10 text-success border border-success/20' :'bg-error/10 text-error border border-error/20'
                    }`}
                  >
                    <Icon
                      name={isSubscriptionActive(clientData.subscription_end) ? 'CheckCircleIcon' : 'XCircleIcon'}
                      size={18}
                    />
                    <span>
                      {isSubscriptionActive(clientData.subscription_end) ? 'الاشتراك ساري' : 'الاشتراك منتهي'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoRow label="الاسم" value={clientData.name} icon="UserIcon" />
                <InfoRow label="رقم الهوية" value={clientData.national_id} icon="IdentificationIcon" />
                <InfoRow label="نوع البطاقة" value={getCardTypeLabel(clientData.card_type)} icon="CreditCardIcon" />
                <InfoRow label="القسم / الجهة" value={clientData.department} icon="BuildingOfficeIcon" />
                <InfoRow label="المدينة" value={clientData.city} icon="MapPinIcon" highlight={false} />
                <InfoRow
                  label="تاريخ انتهاء الاشتراك"
                  value={formatDate(clientData.subscription_end)}
                  icon="CalendarIcon"
                  highlight={!isSubscriptionActive(clientData.subscription_end)}
                />
              </div>

              {visitLogged && (
                <div className="mt-4 flex items-center gap-2 p-3 rounded-md bg-success/10 border border-success/20">
                  <Icon name="CheckCircleIcon" size={18} className="text-success" />
                  <p className="text-sm text-success">تم تسجيل الزيارة بنجاح</p>
                </div>
              )}

              {/* Payment Amount Inputs */}
              {visitLogged && (
                <div className="mt-4 p-4 rounded-md bg-primary/5 border border-primary/20">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="BanknotesIcon" size={18} className="text-primary" />
                    تسجيل المبالغ
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    {/* Amount Before Discount */}
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">المبلغ قبل الخصم</label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={amountBeforeDiscount}
                          onChange={(e) => { setAmountBeforeDiscount(e.target.value); setAmountSaved(false); }}
                          placeholder="0.00"
                          className="w-full px-4 py-2.5 pr-12 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium"
                          disabled={isSavingAmount || amountSaved}
                        />
                        <Icon
                          name="CurrencyDollarIcon"
                          size={18}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                      </div>
                    </div>
                    {/* Amount After Discount */}
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">المبلغ بعد الخصم</label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={amountAfterDiscount}
                          onChange={(e) => { setAmountAfterDiscount(e.target.value); setAmountSaved(false); }}
                          placeholder="0.00"
                          className="w-full px-4 py-2.5 pr-12 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium"
                          disabled={isSavingAmount || amountSaved}
                        />
                        <Icon
                          name="CurrencyDollarIcon"
                          size={18}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleSaveAmount}
                    disabled={isSavingAmount || !amountBeforeDiscount.trim() || !amountAfterDiscount.trim() || amountSaved}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-primary-foreground font-medium transition-premium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSavingAmount ? (
                      <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
                    ) : amountSaved ? (
                      <Icon name="CheckCircleIcon" size={18} />
                    ) : (
                      <Icon name="CheckIcon" size={18} />
                    )}
                    <span>{isSavingAmount ? 'جاري الحفظ...' : amountSaved ? 'تم الحفظ' : 'حفظ المبلغ'}</span>
                  </button>
                  {amountSaved && (
                    <p className="mt-2 text-xs text-success flex items-center gap-1">
                      <Icon name="CheckCircleIcon" size={14} />
                      تم حفظ المبلغ بنجاح في سجل الزيارة
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Partner's Previous Visits Section */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-8 mt-8">
          <h2 className="text-lg font-bold text-foreground font-amiri mb-4 flex items-center gap-2">
            <Icon name="ClockIcon" size={22} className="text-primary" />
            الزيارات السابقة عندك
            {!isLoadingPartnerVisits && (
              <span className="text-sm font-normal text-muted-foreground">({partnerVisits.length} زيارة)</span>
            )}
          </h2>

          {isLoadingPartnerVisits ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
              <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
              <span>جاري تحميل الزيارات...</span>
            </div>
          ) : partnerVisits.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="ClipboardDocumentListIcon" size={40} className="mx-auto mb-2 opacity-30" />
              <p>لا توجد زيارات سابقة مسجلة</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">#</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">اسم العميل</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">تاريخ الزيارة</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">قبل الخصم</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">بعد الخصم</th>
                  </tr>
                </thead>
                <tbody>
                  {partnerVisits.map((visit, index) => (
                    <tr key={visit.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-3 px-4 text-muted-foreground">{index + 1}</td>
                      <td className="py-3 px-4 font-medium">{visit.client_name || '—'}</td>
                      <td className="py-3 px-4">{formatDateTime(visit.scanned_at)}</td>
                      <td className="py-3 px-4">
                        {visit.amount_before_discount != null && visit.amount_before_discount > 0
                          ? `${visit.amount_before_discount} ر.س`
                          : '—'}
                      </td>
                      <td className="py-3 px-4">
                        {visit.amount_after_discount != null && visit.amount_after_discount > 0
                          ? `${visit.amount_after_discount} ر.س`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface InfoRowProps {
  label: string;
  value: string | null | undefined;
  icon: string;
  highlight?: boolean;
}

const InfoRow = ({ label, value, icon, highlight = false }: InfoRowProps) => (
  <div className="flex items-center gap-3 p-3 rounded-md bg-muted/30">
    <Icon name={icon as any} size={18} className="text-primary flex-shrink-0" />
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`font-medium truncate ${highlight ? 'text-error' : 'text-foreground'}`}>
        {value || '—'}
      </p>
    </div>
  </div>
);

export default PartnerDashboard;
